// §9.3 `menu` + §6.3 — the in-window menu bar.
//
// §6.3 gives house and windows11 the *same* answer for menus — "in-window menu bar" — so this is
// one component both mount rather than two that have to be kept in step. Each skin styles it; the
// behaviour, the keyboard handling and the `command/fire` delivery are the same because the spec
// says they are. macOS (M11) puts the identical tree in a *global* bar, and reads it from the same
// `kernel.menus` service — which is why the service is keyed by window instance and this component
// takes the window as a prop.
//
// It renders inside `.win-chrome`, under the title bar. That matters for two reasons: the chrome is
// the skin's portal target so a skin change redraws the menu without touching the app's frame
// (§5.7), and pointer events here must not reach the title bar's drag handler.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MenuNode, Win } from '@hangar/shared';
import { COPY } from '@/copy';
import { useFocusedWindowId, useKernel, useStoreValue } from '@/kernel/react';

/** A leaf fires; a node with children opens. One predicate, so the two never disagree. */
function isSubmenu(node: MenuNode): boolean {
  return Array.isArray(node.items) && node.items.length > 0;
}

function MenuItems({
  nodes,
  onFire,
  depth,
}: {
  nodes: readonly MenuNode[];
  onFire: (id: string) => void;
  depth: number;
}) {
  const [openSub, setOpenSub] = useState<string | null>(null);

  return (
    <ul className="win-menu-list" role="menu" data-depth={depth}>
      {nodes.map((node) => {
        const submenu = isSubmenu(node);
        const disabled = node.enabled === false;
        return (
          <li
            key={node.id}
            className="win-menu-item-row"
            data-separator-before={node.separatorBefore ? 'true' : undefined}
            onPointerEnter={() => setOpenSub(submenu && !disabled ? node.id : null)}
          >
            <button
              type="button"
              className="win-menu-item"
              role={node.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              data-testid={`menu-item-${node.id}`}
              data-submenu={submenu ? 'true' : undefined}
              disabled={disabled}
              aria-disabled={disabled || undefined}
              aria-checked={node.checked === undefined ? undefined : node.checked}
              aria-haspopup={submenu || undefined}
              onClick={() => {
                if (disabled) return;
                // A submenu opens on hover and has nothing of its own to fire.
                if (submenu) {
                  setOpenSub(node.id);
                  return;
                }
                onFire(node.id);
              }}
            >
              <span className="win-menu-check" aria-hidden>
                {node.checked ? '✓' : ''}
              </span>
              <span className="win-menu-label">{node.label}</span>
              {node.accelerator ? (
                <span className="win-menu-accel" aria-hidden>
                  {node.accelerator}
                </span>
              ) : null}
              {submenu ? (
                <span className="win-menu-arrow" aria-hidden>
                  ›
                </span>
              ) : null}
            </button>

            {submenu && openSub === node.id ? (
              <div className="win-menu-flyout" data-testid={`menu-flyout-${node.id}`}>
                <MenuItems nodes={node.items ?? []} onFire={onFire} depth={depth + 1} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The tree a window instance has published, kept in step with `app:menuPublished`. */
function useMenuTree(win: Win | undefined): readonly MenuNode[] {
  const kernel = useKernel();
  const [tree, setTree] = useState<readonly MenuNode[]>(() =>
    win ? kernel.menus.of(win.instanceKey) : [],
  );

  // The tree arrives asynchronously — an app publishes when it is ready, which is after its window
  // exists. `app:menuPublished` is the only thing that changes it.
  useEffect(() => {
    if (!win) {
      setTree([]);
      return;
    }
    const read = (): void => setTree(kernel.menus.of(win.instanceKey));
    read();
    return kernel.bus.on('app:menuPublished', ({ winId }) => {
      if (winId === win.id) read();
    });
  }, [kernel, win]);

  return tree;
}

/** Close an open menu on a click anywhere outside `root`, and on Escape. */
function useDismissOnOutside(
  open: string | null,
  root: React.RefObject<HTMLElement | null>,
  close: () => void,
): void {
  useEffect(() => {
    if (open === null) return;
    // A click *inside the app's own frame* has to close it too, which is why this listens on the
    // document rather than on a backdrop.
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current?.contains(event.target as Node)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, root, close]);
}

/** The root buttons and their dropdowns. Identical in every bar that renders a §9.3 tree. */
function MenuRoots({
  tree,
  open,
  setOpen,
  onFire,
}: {
  tree: readonly MenuNode[];
  open: string | null;
  setOpen: (next: string | null | ((current: string | null) => string | null)) => void;
  onFire: (id: string) => void;
}) {
  return (
    <>
      {tree.map((node) => (
        <div className="win-menu-root" key={node.id}>
          <button
            type="button"
            className="win-menu-title"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={open === node.id}
            data-testid={`menu-${node.id}`}
            data-open={String(open === node.id)}
            onClick={() => setOpen((current) => (current === node.id ? null : node.id))}
            // Once one menu is open, moving across the bar opens the next — as a menu bar does.
            onPointerEnter={() => setOpen((current) => (current === null ? null : node.id))}
          >
            {node.label}
          </button>

          {open === node.id ? (
            <div className="win-menu-dropdown" data-testid={`menu-dropdown-${node.id}`}>
              <MenuItems nodes={node.items ?? []} onFire={onFire} depth={0} />
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
}

export function WindowMenuBar({ win }: { win: Win }) {
  const kernel = useKernel();
  const tree = useMenuTree(win);
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useDismissOnOutside(open, root, close);

  // A window with no published menu has no bar at all — the app gets the space back.
  if (tree.length === 0) return null;

  const fire = (id: string): void => {
    setOpen(null);
    kernel.fireCommand(win.instanceKey, id);
  };

  return (
    <div
      className="win-menubar"
      role="menubar"
      data-testid={`menubar-${win.id}`}
      ref={root}
      // The title bar above starts a drag on `pointerdown` and calls `preventDefault()`, which
      // suppresses the compatibility click (D-107). The menu bar is a sibling rather than a child,
      // but a stray pointerdown reaching a drag surface is the same bug, so it stops here.
      onPointerDown={(event) => event.stopPropagation()}
    >
      <MenuRoots tree={tree} open={open} setOpen={setOpen} onFire={fire} />
    </div>
  );
}

/**
 * §6.3 / §9.3 — the **global** menu bar: the focused window's published tree, in a bar that belongs
 * to the desktop rather than to the window.
 *
 * The same tree, the same `kernel.menus` service and the same dropdowns as `WindowMenuBar` — which
 * is the point of §9.3's `menu` channel being window-scoped rather than app-scoped. All that
 * differs is *where the tree comes from*: this reads whichever window has focus, and swaps when
 * focus moves. An app publishes once and gets both.
 *
 * `appName` is rendered first and in bold, as the platform does. It is not part of the app's tree —
 * an app cannot publish it, and cannot remove it.
 */
export function GlobalMenuBar() {
  const kernel = useKernel();
  const focusedId = useFocusedWindowId();
  const focused = useStoreValue(kernel.wm.store, (state) =>
    focusedId === null ? undefined : state.windows[focusedId],
  );
  const tree = useMenuTree(focused);
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useDismissOnOutside(open, root, close);

  // Focus moving is a different menu bar, so whatever was open belongs to a window that is no
  // longer the subject.
  useEffect(() => setOpen(null), [focusedId]);

  const fire = (id: string): void => {
    setOpen(null);
    if (focused) kernel.fireCommand(focused.instanceKey, id);
  };

  return (
    <div className="win-menubar macos-menubar-menus" role="menubar" data-testid="global-menubar" ref={root}>
      <span className="macos-menubar-app" data-testid="global-menubar-app">
        {focused ? kernel.appName(focused.appId) : COPY.chrome.desktopName}
      </span>
      {focused ? <MenuRoots tree={tree} open={open} setOpen={setOpen} onFire={fire} /> : null}
    </div>
  );
}
